import { TrainingMaterialsReport } from './../../documents/models/report';
import { ColDef } from 'ag-grid-community';
import { Moment, duration } from 'moment';
import { AdminSiteActionActivityReport } from '../../admin/activity-reports/report-types/admin-site-action-activity-report';
import { CovanceGridColumn, dateRenderer, dateTimeComparator, dateTimeRenderer } from '../../covance-ag-grid';
import { SponsorSiteActionActivityReport } from '../../sponsor/activity-reports/report-types/sponsor-site-action-activity-report';
import { AdminLabReportActivityReport } from '../../admin/activity-reports/report-types/admin-lab-report-activity-report';
import { SponsorLabReportActivityReport } from '../../sponsor/activity-reports/report-types/sponsor-lab-report-activity-report';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { AdminLabManualActivityReport } from '../../admin/activity-reports/report-types/admin-lab-manual-activity-report';
import { SponsorLabManualActivityReport } from '../../sponsor/activity-reports/report-types/sponsor-lab-manual-activity-report';
import { Category } from '../../documents/models/category';
import { comparators } from '../../core/comparators';
import { ActivityReportEQuery } from '../../admin/activity-reports/report-types/activity-report-e-query';
import { ResponseAgeRendererComponent } from '../../covance-ag-grid/renderers/response-age-renderer/response-age-renderer.component';

export const userNameColumn: ColDef = {
    headerName: 'User Name',
    field: 'userName',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const emailColumn: ColDef = {
    headerName: 'Email',
    field: 'email',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const blindingRoleColumn: ColDef = {
    headerName: 'Blinding Role',
    field: 'blindingRole',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const activeSiteColumn: ColDef = {
    headerName: 'Site Status',
    field: 'activeFlag',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const studyRoleColumn: ColDef = {
    headerName: 'Study Role',
    field: 'studyRole',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const countryColumn: ColDef = {
    headerName: 'Country',
    field: 'country',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const sponsorNameColumn: ColDef = {
    field: 'sponsorName',
    headerName: 'Sponsor',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const protocolNameColumn: ColDef = {
    headerName: 'Protocol',
    field: 'protocolName',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    comparator: (a, b) => comparators.mixedStringComparator(a, b),
};

export const protocolNumberColumn: ColDef = {
    headerName: 'Protocol #',
    field: 'protocolNumber',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    comparator: (a, b) => comparators.mixedStringComparator(a, b),
};

export const siteSpecificColumn: ColDef = {
    headerName: 'Site Specific',
    field: 'sites',
    valueGetter: (params: { data: TrainingMaterialsReport }) => (params.data.sites || []).map(s => s.siteNum).join(', '),
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    comparator: (a, b) => comparators.mixedStringComparator(a, b),
};

export function getSiteNameColumn(sort: 'asc' | 'desc' | null = null): ColDef {
    return {
        headerName: 'Site Name',
        sort: sort,
        field: 'siteName',
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true }
    };
}

export function getCompositeSiteColumn(sort: 'asc' | 'desc' | null = null): ColDef {
    return {
        headerName: 'Site',
        sort: sort,
        field: 'siteDisplayName',
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true }
    };
}

export const visitColumn: ColDef = {
    headerName: 'Visit',
    field: 'visit',
    filter: 'agTextColumnFilter',
};

type SiteActionOrLabReportActivityReport = AdminLabReportActivityReport | SponsorLabReportActivityReport | AdminSiteActionActivityReport | SponsorSiteActionActivityReport;

export const visitCollectionDateColumn: ColDef = {
    headerName: 'Visit Collection Date',
    field: 'visitCollectionDate',
    filter: 'agTextColumnFilter',
    comparator: dateTimeComparator,
    valueGetter: (params: { data: SiteActionOrLabReportActivityReport }) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.visitCollectionDate });
    },
};

export const accessionNumberColumns: ColDef = {
    headerName: 'Accession',
    field: 'accessionNumber',
    filter: 'agTextColumnFilter',
};

export const subcategoryTitleColumn: ColDef = {
    headerName: 'Subcategory',
    field: 'subcategoryTitle',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const subjectNumberAndInitialsColumn: ColDef = {
    headerName: 'Subject',
    field: 'fakeSubjectId', // Warn: Using fake FieldId here since AG export skips field without FieldId. 
    valueGetter: (params: { data: SiteActionOrLabReportActivityReport }) => {
        if (!params.data) {
            return null;
        }

        const subjectTitle = params.data.zcSubjectId ?  SubjectTitleRenderer.renderTitle({
            data: {
                subjectNumber: params.data.subjectNumber,
                initials: params.data.subjectInitials,
                screeningNumber: params.data.screeningNumber,
                zcSubjectId: params.data.zcSubjectId
            }
        }) : '';

        return subjectTitle;
    },
};

export const eQueryIdColumn: ColDef = {
    headerName: 'eQueryId',
    field: 'eQueryId',
    width: 70,
    comparator: (a, b) => comparators.numberComparator(a, b)
};

export const createDateColumn: ColDef = {
    headerName: 'Create Date',
    field: 'createDate',
    width: 70,
    comparator: dateTimeComparator,
    valueGetter: (params: { data: ActivityReportEQuery }) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.createDate });
    }
};

export const responseDateColumn: ColDef = {
    headerName: 'Response Date Time',
    field: 'responseDate',
    width: 70,
    comparator: dateTimeComparator,
    valueGetter: (params: { data: ActivityReportEQuery }) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.responseDate });
    }
};

export const issueTypesColumn: ColDef = {
    headerName: 'Issue Type',
    field: 'issueTypes',
    width: 70,
    comparator: comparators.stringComparator,
};

export const statusColumn: ColDef = {
    headerName: 'Status',
    field: 'status',
    width: 70,
    comparator: (a, b) => comparators.numberComparator(a, b)
};

export const responseAgeColumn: ColDef = {
    headerName: 'Time Before Response',
    field: 'responseAge',
    width: 70,
    cellRendererFramework: ResponseAgeRendererComponent,
    comparator: comparators.distanceComparator,
    filterValueGetter: (params: { data: ActivityReportEQuery } ) => params.data.responseAge ? params.data.responseAge.formattedValue : null
};

export const readDateTimeColumn: ColDef = {
    headerName: 'Last Read',
    field: 'accessDateTime',
    comparator: dateTimeComparator,
    valueGetter: (params: { data: any }) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.accessDateTime });
    },
    filter: 'agTextColumnFilter'
};

export const documentTypeColumn: ColDef = {
    headerName: 'Document Type',
    field: 'documentType',
    filter: 'agTextColumnFilter',
};

export const projectNumberColumn: ColDef = {
    headerName: 'Project',
    field: 'projectNumber',
    filter: 'agTextColumnFilter',
};

export const siteNumberColumn: ColDef = {
    headerName: 'Site Number',
    field: 'siteNum',
    filter: 'agTextColumnFilter',
};

export const descriptionColumn: ColDef = {
    headerName: 'Description',
    field: 'description',
    filter: 'agTextColumnFilter',
};

export const notifyTypeColumn: ColDef = {
    headerName: 'Notify Type',
    field: 'notifyType',
    filter: 'agTextColumnFilter',
};

export const emailDateTimeColumn: ColDef = {
    headerName: 'Sent Time',
    field: 'emailDateTime',
    comparator: dateTimeComparator,
    valueGetter: (params: { data: any }) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.emailDateTime });
    },
    filter: 'agTextColumnFilter'
};

export const emailSentToColumn: ColDef = {
    headerName: 'Sent To',
    field: 'emailSentTo',
    filter: 'agTextColumnFilter',
};

export const timeBetweenReadAndPosted: ColDef = {
    headerName: 'Time Between Read and Posted',
    field: 'fakeDateId', // Warn: Using fake FieldId here since AG export skips field without FieldId.
    valueGetter: (params: { data: SiteActionOrLabReportActivityReport }) => {
        if (!params.data || !params.data.accessDateTime) {
            return null;
        }
        const timeSpan = duration(params.data.accessDateTime.diff(params.data.postedDateTime));
        return new TimeBetweenReadAndPosted(Math.floor(timeSpan.asDays()), timeSpan.hours());
    },
    comparator: (a: TimeBetweenReadAndPosted, b: TimeBetweenReadAndPosted) => {
        if (a && !b) {
            return 1;
        }
        if (!a && b) {
            return -1;
        }
        if (!a && !b) {
            return 0;
        }
        if (a.value > b.value) {
            return 1;
        }
        if (a.value < b.value) {
            return -1;
        }
        return 0;
    }
};

class TimeBetweenReadAndPosted {
    private readonly numVal: number;
    private readonly strVal: string;

    public get value() {
        return this.numVal;
    }

    constructor(days: number, hours: number) {
        this.numVal = days > 0 ? days * 24 + hours : hours;
        this.strVal = days > 0 ? `${days} d ${hours} h` : `${hours} h`;
    }

    public toString() {
        return this.strVal;
    }
}

export function buildDateColumn<T extends { [k in K]: Moment }, K extends keyof T>(field: K, label: string, sort: 'asc' | 'desc' | null = null): ColDef {
    return {
        headerName: label,
        sort: sort,
        field: field.toString(),
        comparator: dateTimeComparator,
        valueGetter: (params: { data: T }) => {
            if (!params.data) {
                return null;
            }

            return dateRenderer({ value: params.data[field] });
        },
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter'
    };
}

export function buildDateTimeColumn<T extends { [k in K]: Moment }, K extends keyof T>(field: K, label: string): ColDef {
    return {
        headerName: label,
        field: field.toString(),
        comparator: dateTimeComparator,
        valueGetter: (params: { data: T }) => {
            if (!params.data) {
                return null;
            }

            return dateTimeRenderer({ value: params.data[field] });
        },
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter'
    };
}

export function getActivityReportsVersionColumn<T extends AdminLabManualActivityReport | SponsorLabManualActivityReport>(): CovanceGridColumn {
    return {
        headerName: 'Version Number/Date/Title',
        field: 'version',
        minWidth: 155,
        valueGetter: (params: { data: T }) => {
            const report = params.data;
            if (!report) {
                return null;
            }

            if (Category.isSynopsis(report.subcategoryId)) {
                return dateRenderer({ value: report.versionDate });
            } else if (Category.isLabManualsOthers(report.subcategoryId)) {
                return report.title;
            } else {
                return report.version;
            }
        },
    };
}
